import { ApiError } from '@/lib/http';
import { failureCopy, type FailureCopy, type FailureCopyInput } from './failure-copy';

const GENERIC = 'Something went wrong on our side.';
const READ_ONLY = 'This workspace is read-only, so nothing new can be added.';

export function regeneratingHeadline(outputType: 'text' | 'canvas', templateName: string | null): string {
  const verb = outputType === 'canvas' ? 'Drawing' : 'Writing';
  return templateName ? `${verb} a new version of ${templateName}…` : `${verb} a new version…`;
}

export function regeneratingDetail(attempt?: number): string {
  if (attempt !== undefined && attempt >= 2) return 'Taking longer than usual. Trying again.';
  return 'The current version stays until you review the new one.';
}

export function waitingLine(templateName: string | null): string {
  return templateName ? `A new version of ${templateName} is ready.` : 'A new version is ready.';
}

export const FROM_EXCERPT_LINE = 'Made from the saved passage, not the current text.';
export const STALE_PROPOSAL_LINE = "This new version isn't available any more.";
export const CONFLICT_SENTENCE = "This changed since the new version was written, so it wasn't replaced.";
export const UNPLACEABLE_SENTENCE = "This heading appears more than once, so the new version can't be placed here.";

export function regenerationFailedCopy(
  input: FailureCopyInput & { surfaceTitle: string },
): FailureCopy {
  const { reason, retry } = failureCopy(input);
  return {
    headline:
      input.outputType === 'canvas' ? "Couldn't draw a new version." : "Couldn't write a new version.",
    safety: `Nothing in ${input.surfaceTitle} changed.`,
    reason,
    retry,
  };
}

function sections(count: number): string {
  return count === 1 ? 'section' : 'sections';
}

export function reviewTitle(templateName: string | null): string {
  return templateName ? `Reviewing a new version of ${templateName}` : 'Reviewing a new version';
}

export function progressLine(changed: number, total: number, decided: number): string {
  const base = `${changed} of ${total} ${sections(total)} changed`;
  if (changed === 0) return base;
  if (decided === 0) return `${base} · none decided yet`;
  if (decided >= changed) return `${base} · all decided`;
  return `${base} · ${decided} decided`;
}

export function applyLabel(toApply: number, allDecided: boolean): string {
  if (!allDecided) return 'Apply';
  if (toApply === 0) return 'Done';
  return `Apply ${toApply} ${toApply === 1 ? 'change' : 'changes'}`;
}

export const UNDECIDED_LINE = 'Undecided changes stay as they are.';
export const KEYBOARD_HINT = 'J K next / previous change · U use new · C keep current';
export const PEEK_HINT = 'Hold Space to see the current one.';

export function conflictSummary(applied: number, conflicted: number): string {
  const verb = conflicted === 1 ? 'needs' : 'need';
  if (applied === 0) return `${conflicted} ${sections(conflicted)} ${verb} you`;
  return `${applied} ${sections(applied)} applied, ${conflicted} ${verb} you`;
}

export function latestVersionHint(templateName: string): string {
  return `${templateName} has a newer version`;
}

export const REVIEW_LABELS = {
  reviewing: 'Reviewing a new version',
  review: 'Review',
  dismiss: 'Dismiss',
  tryAgain: 'Try again',
  close: 'Close',
  keepAll: 'Keep all',
  useAll: 'Use all',
  apply: 'Apply',
  done: 'Done',
  keepCurrent: 'Keep current',
  useNew: 'Use new',
  useNewAnyway: 'Use new anyway',
  undo: 'Undo',
  usingNew: 'Using new',
  keepingCurrent: 'Keeping current',
  noChange: 'No change',
  applied: 'Applied',
  kept: 'Kept',
  regenerate: 'Regenerate',
  regenerateHint: 'Same template, current content',
  regenerateLatest: 'Regenerate with the latest version',
  chipChanged: 'Changed',
  chipNewSection: 'New section',
  chipRemoved: 'Removed in the new version',
  showCurrent: 'Show current',
  hideCurrent: 'Hide current',
  paneCurrent: 'Current when this was written',
  paneNew: 'New',
  viewCurrent: 'Current',
  viewNew: 'New',
  versionShown: 'Version shown',
  diffAdded: 'added:',
  diffRemoved: 'removed:',
  formattingChanged: 'Formatting changed',
} as const;

export type ReviewLabel = keyof typeof REVIEW_LABELS;

export function regenerateRefusalMessage(
  error: unknown,
  request: { useLatestVersion: boolean },
): string | null {
  if (!(error instanceof ApiError)) return GENERIC;
  switch (error.status) {
    case 402:
      return READ_ONLY;
    case 403:
      return 'You need edit access to this document, and access to what it was made from, to regenerate it.';
    case 404:
      return error.code === 'template_not_found'
        ? "This template isn't available any more."
        : "Something this was made from can't be found any more.";
    case 409:
      if (error.code === 'surface_busy') return null;
      return error.code === 'not_generated_by_template' ? "This can't be regenerated." : GENERIC;
    case 422:
      return "The version this was made with isn't available any more.";
    case 400:
      return request.useLatestVersion
        ? "The latest version of this template can't be made from what this was made from."
        : GENERIC;
    default:
      return GENERIC;
  }
}

export type ReviewFailure = 'closed' | 'replaced' | 'removed' | { message: string };

export const UNCERTAIN_APPLY_LINE = 'This may have gone through. Checking…';

/** An Apply that timed out may have landed: read the proposal again. */
export function applyFailure(error: unknown): ReviewFailure | 'uncertain' {
  if (error instanceof ApiError && error.status === 0) return 'uncertain';
  return reviewFailure(error);
}

export function reviewFailure(error: unknown): ReviewFailure {
  if (!(error instanceof ApiError)) return { message: GENERIC };
  switch (error.status) {
    case 402:
      return { message: READ_ONLY };
    case 403:
      return { message: 'You need edit access to this document to apply this.' };
    case 404:
      return 'closed';
    case 409:
      switch (error.code) {
        case 'proposal_closed':
          return 'closed';
        case 'proposal_replaced':
          return 'replaced';
        case 'surface_removed':
          return 'removed';
        case 'proposal_unavailable':
          return {
            message:
              "The person who asked for this can't edit this document any more, so it can't be applied.",
          };
        default:
          return { message: GENERIC };
      }
    default:
      return { message: GENERIC };
  }
}
